import { useTranslation } from "react-i18next";

import { ChartLineIcon } from "../../Icons";
import { TooltipWrapper } from "../../Tooltip";

type AnalyticsIconButtonProps = {
  /** Row subject named in the accessible label — a Sub IB or program name. */
  subject: string;
  onClick: () => void;
  /** Per-surface sizing; the two tables and the programs cell differ. */
  className?: string;
};

/**
 * Brand-blue chart button that opens the row's performance report.
 *
 * Mirrors the canvas's `AnalyticsIconButton` (`TableRow.tsx`): one shared
 * "View performance" hover tooltip, with the accessible label naming the row so
 * screen-reader users can tell the buttons apart. Both strings come from the
 * canvas copy registry key `action.viewPerformance` — the previous inline
 * buttons said "View analytics", which is not the design's wording.
 */
export const AnalyticsIconButton = ({
  subject,
  onClick,
  className,
}: AnalyticsIconButtonProps) => {
  const { t } = useTranslation();

  return (
    <TooltipWrapper
      text={t("subIBReport_viewPerformance", "View performance")}
      color="blue"
      tooltipPlacement="top"
      // Sits in a table cell, so the scroll wrapper bounds `flip()` — on the
      // first row there isn't a tooltip's worth of room under the header and
      // `top` would bounce to bottom.
      positionStrategy="fixed"
      tooltipMaxWidth={240}
      tooltipContainerClassName="!text-xs"
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={t(
          "subIBReport_viewPerformanceFor",
          "View performance for {{subject}}",
          { subject },
        )}
        className={className}
      >
        <ChartLineIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </TooltipWrapper>
  );
};
